import { useIsHydrated } from '@core/shared';
import { EmailTemplateType, OptionsApi } from '@core/swagger-client';
import { Button, Input, TextArea } from '@core/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PreviewPanelOrganism } from '../../organism';

interface Props {
    subject: string;
    body: string;
    readOnly?: boolean;
    onSubjectChange: (value: string) => void;
    onBodyChange: (value: string) => void;
    onSave: () => void;
    isSaving: boolean;
}

export const EmailTemplateTab = ({
    subject,
    body,
    readOnly,
    onSubjectChange,
    onBodyChange,
    onSave,
    isSaving
}: Props) => {
    const { t } = useTranslation();
    const isHydrated = useIsHydrated();

    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewSubject, setPreviewSubject] = useState('');
    const [previewBody, setPreviewBody] = useState('');
    const [previewedFor, setPreviewedFor] = useState<{ subject: string; body: string }>();

    const previewMutation = useMutation(OptionsApi.emailLists.previewTemplate());

    useEffect(() => {
        if (!subject || !body) {
            setPreviewLoading(false);
            setPreviewSubject('');
            setPreviewBody('');
            return;
        }
        if (previewMutation.isPending) return;
        if (previewedFor?.subject === subject && previewedFor?.body === body) return;

        setPreviewLoading(true);
        const handle = setTimeout(() => {
            previewMutation.mutate(
                { templateType: EmailTemplateType.WebinarEmailList, subject, body },
                {
                    onSuccess: (rendered) => {
                        setPreviewLoading(false);
                        setPreviewedFor({ subject, body });
                        setPreviewSubject(rendered.subject);
                        setPreviewBody(rendered.body);
                    },
                    onError: () => {
                        setPreviewLoading(false);
                        setPreviewedFor({ subject, body });
                        setPreviewSubject('');
                        setPreviewBody('');
                    }
                }
            );
        }, 500);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subject, body, previewedFor, previewMutation.isPending]);

    if (readOnly) {
        return (
            <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1 flex flex-col gap-4">
                    <PreviewPanelOrganism isLoading={previewLoading} subject={previewSubject} html={previewBody} />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1 flex flex-col gap-4">
                    <Input
                        data-testid="campaign-subject"
                        label={t('EmailLists.Send.SubjectLabel')}
                        value={subject}
                        onChange={(event) => onSubjectChange(event.target.value)}
                    />
                    <TextArea
                        data-testid="campaign-body"
                        label={t('EmailLists.Send.BodyLabel')}
                        value={body}
                        onChange={(event) => onBodyChange(event.target.value)}
                        textAreaClassNames="min-h-[30vh] field-sizing-content"
                    />
                </div>
                <div className="flex-1 flex flex-col">
                    <PreviewPanelOrganism isLoading={previewLoading} subject={previewSubject} html={previewBody} />
                </div>
            </div>

            <div className="mt-6">
                <Button
                    data-testid="campaign-save"
                    variant="secondary"
                    isLoading={isSaving}
                    disabled={!isHydrated}
                    onClick={onSave}
                >
                    {t('Generic.Button.Save')}
                </Button>
            </div>
        </>
    );
};
